import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/utils/api";
import { trackEvent } from "@/lib/analytics";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
// Feb 2026 — DACH-Smart-Inputs (Phase 5)
import { IBANInput, VATInput, PostcodeInput } from "@/components/inputs/SmartInputs";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Globe,
  Building,
  CreditCard,
  ChevronRight,
  ChevronLeft,
  Check,
  Upload,
  UserPlus,
  Truck,
  ArrowRight,
  Hash,
  X,
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";

const BUNDESLAENDER = [
  { code: "BW", name: "Baden-Württemberg" }, { code: "BY", name: "Bayern" },
  { code: "BE", name: "Berlin" }, { code: "BB", name: "Brandenburg" },
  { code: "HB", name: "Bremen" }, { code: "HH", name: "Hamburg" },
  { code: "HE", name: "Hessen" }, { code: "MV", name: "Mecklenburg-Vorpommern" },
  { code: "NI", name: "Niedersachsen" }, { code: "NW", name: "Nordrhein-Westfalen" },
  { code: "RP", name: "Rheinland-Pfalz" }, { code: "SL", name: "Saarland" },
  { code: "SN", name: "Sachsen" }, { code: "ST", name: "Sachsen-Anhalt" },
  { code: "SH", name: "Schleswig-Holstein" }, { code: "TH", name: "Thüringen" },
];

const KANTONE = [
  "Zürich", "Bern", "Luzern", "Uri", "Schwyz", "Obwalden", "Nidwalden",
  "Glarus", "Zug", "Freiburg", "Solothurn", "Basel-Stadt", "Basel-Landschaft",
  "Schaffhausen", "Appenzell Ausserrhoden", "Appenzell Innerrhoden",
  "St. Gallen", "Graubünden", "Aargau", "Thurgau", "Tessin", "Waadt",
  "Wallis", "Neuenburg", "Genf", "Jura",
];

const COUNTRIES = [
  { code: "DE", name: "Deutschland", flag: "DE" },
  { code: "AT", name: "Österreich", flag: "AT" },
  { code: "CH", name: "Schweiz", flag: "CH" },
];

const ALL_STEPS = [
  { key: "country", label: "Dein Land", icon: Globe },
  { key: "business", label: "Dein Business", icon: Building },
  { key: "billing", label: "Rechnungsdaten", icon: CreditCard },
  { key: "migration", label: "Umzug (optional)", icon: Truck },
];

export default function OnboardingWizard({ onComplete, startAtStep = 0, detectedCountry, completeProfileMode = false, onClose }) {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(startAtStep);
  const [saving, setSaving] = useState(false);
  const [wantsMigration, setWantsMigration] = useState(null);   // true | false | null (Umzugs-Schritt)
  const [lastInvoiceNumber, setLastInvoiceNumber] = useState("");  // optionale Nummernfortführung

  const [selectedCountry, setSelectedCountry] = useState(
    detectedCountry || user?.country || "DE"
  );

  const [form, setForm] = useState({
    company_name: "",
    is_kleinunternehmer: false,
    vstp_enabled: false,
    tax_id: "",
    bundesland: "",
    church_tax: false,
    canton: "",
    svs_opt_out: false,
    trade_tax_liable: false,
    street: "",
    street_number: "",
    postal_code: "",
    city: "",
    bank_name: "",
    iban: "",
    bic: "",
  });

  // Pre-fill form with existing settings so users don't have to re-enter data
  useEffect(() => {
    api.get("/settings").then((res) => {
      const s = res.data;
      if (!s) return;
      setForm((f) => ({
        ...f,
        company_name:      s.company_name      || f.company_name,
        is_kleinunternehmer: s.is_kleinunternehmer ?? f.is_kleinunternehmer,
        tax_id:            s.tax_id            || s.vat_id || f.tax_id,
        bundesland:        s.bundesland        || f.bundesland,
        church_tax:        s.church_tax        ?? f.church_tax,
        canton:            s.canton            || f.canton,
        svs_opt_out:       s.svs_opt_out       ?? f.svs_opt_out,
        trade_tax_liable:  s.trade_tax_liable  ?? f.trade_tax_liable,
        street:            s.street            || f.street,
        street_number:     s.street_number     || f.street_number,
        postal_code:       s.postal_code       || f.postal_code,
        city:              s.city              || f.city,
        bank_name:         s.bank_name         || f.bank_name,
        iban:              s.iban              || f.iban,
        bic:               s.bic              || f.bic,
      }));
      if (s.country) setSelectedCountry(s.country);
      // Bereits gespeichertes Logo in die Vorschau laden (base64 wie auf der Settings-Seite),
      // sonst wirkt es beim erneuten Öffnen so, als wäre das Logo nicht gespeichert.
      if (s.logo_base64) {
        const mime = s.logo_url?.includes(".png") ? "image/png"
          : (s.logo_url?.includes(".jpg") || s.logo_url?.includes(".jpeg")) ? "image/jpeg"
          : s.logo_url?.includes(".webp") ? "image/webp" : "image/png";
        setLogoPreview(`data:${mime};base64,${s.logo_base64}`);
      }
    }).catch(() => {}); // silently ignore if settings not yet available

    // Bundesland / Kirchensteuer / Kanton / SVS liegen am User-Objekt (nicht in /settings)
    // → separat aus /country/info laden, sonst zeigt der Wizard sie leer an, obwohl gespeichert.
    api.get("/country/info").then((res) => {
      const c = res.data;
      if (!c) return;
      setForm((f) => ({
        ...f,
        bundesland:  c.bundesland  || f.bundesland,
        church_tax:  c.church_tax  ?? f.church_tax,
        canton:      c.canton      || f.canton,
        svs_opt_out: c.svs_opt_out ?? f.svs_opt_out,
      }));
      if (c.country_code) setSelectedCountry(c.country_code);
    }).catch(() => {});
  }, []);

  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const country = step === 0 ? selectedCountry : selectedCountry;
  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  // Sep 2026 — im Profil-vervollständigen-Modus entfällt der Umzugs-Schritt
  const steps = completeProfileMode ? ALL_STEPS.slice(0, 3) : ALL_STEPS;
  const maxStep = steps.length - 1;

  const isStep0Valid = () => !!selectedCountry;

  const isStep1Valid = () => {
    if (!form.company_name.trim()) return false;
    if (selectedCountry === "DE" && !form.bundesland) return false;
    if (selectedCountry === "CH" && !form.canton) return false;
    return true;
  };

  const isStep2Valid = () => {
    if (!form.street.trim()) return false;
    if (!form.postal_code.trim()) return false;
    if (!form.city.trim()) return false;
    return true;
  };

  const isCurrentStepValid = () => {
    if (step === 0) return isStep0Valid();
    if (step === 1) return isStep1Valid();
    if (step === 2) return isStep2Valid();
    if (step === 3) return true;   // Umzug ist optional
    return false;
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Logo darf maximal 2 MB groß sein");
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");

      // 1. Confirm tax residency (country)
      await axios.post(
        `${API}/country/confirm-tax-residency`,
        null,
        {
          params: { country_code: selectedCountry },
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // 2. Save settings (company, address, bank)
      await api.put("/settings", {
        company_name: form.company_name,
        tax_id: form.tax_id || undefined,
        street: form.street || undefined,
        street_number: form.street_number || undefined,
        postal_code: form.postal_code || undefined,
        city: form.city || undefined,
        country_code: selectedCountry,
        bank_name: form.bank_name || undefined,
        iban: form.iban || undefined,
        bic: form.bic || undefined,
        trade_tax_liable: selectedCountry === "DE" ? form.trade_tax_liable : undefined,
      });

      // 3. Save Kleinunternehmer status
      try {
        await api.put("/user/kleinunternehmer", {
          is_kleinunternehmer: form.is_kleinunternehmer,
          kleinunternehmer_country: form.is_kleinunternehmer ? selectedCountry : null,
        });
      } catch (e) {
        console.warn("Kleinunternehmer update:", e);
      }

      // 4. Country-specific settings (Bundesland, Kirchensteuer, Kanton, SVS)
      try {
        const params = { country_code: selectedCountry };
        if (selectedCountry === "DE") {
          params.church_tax = form.church_tax;
          params.bundesland = form.bundesland;
        }
        if (selectedCountry === "CH") params.canton = form.canton;
        if (selectedCountry === "AT") params.svs_opt_out = form.svs_opt_out;
        await api.put("/country/update", null, { params });
      } catch (e) {
        console.warn("Country update:", e);
      }

      // 4b. Vorsteuerpauschalierung (nur AT, optional, Default AUS)
      if (selectedCountry === "AT" && form.vstp_enabled && !form.is_kleinunternehmer) {
        try {
          await api.put("/vstp/settings", { enabled: true, method: "base" });
        } catch (e) {
          console.warn("VStP update:", e);
        }
      }

      // 5. Upload logo
      if (logoFile) {
        try {
          const logoData = new FormData();
          logoData.append("file", logoFile);
          await api.post("/settings/upload-logo", logoData, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (e) {
          console.warn("Logo upload:", e);
        }
      }

      // 6. Mark onboarding as completed
      await api.put("/auth/me", null, {
        params: { onboarding_completed: true },
      });
      // Conversion-Funnel (PostHog, August 2026)
      trackEvent('onboarding_completed', { migration: Boolean(wantsMigration) });

      // 6b. Optionale Rechnungsnummern-Fortführung (Umsteiger)
      if (wantsMigration && lastInvoiceNumber.trim()) {
        try {
          const r = await api.post("/number-ranges/continue-from", {
            number: lastInvoiceNumber.trim(), mode: "last",
          });
          toast.success(`Nummernkreis übernommen — deine nächste Rechnung: ${r.data.preview}`);
        } catch (e) {
          toast.error(e.response?.data?.detail || "Nummernkreis konnte nicht übernommen werden.");
        }
      }

      await refreshUser();
      onComplete();
      // Sep 2026 — Soft-Onboarding: aus dem „Profil vervollständigen"-Banner
      // geöffnet → keine Navigation, nur bestätigen.
      if (completeProfileMode) {
        toast.success("Profil vervollständigt!");
      } else if (wantsMigration) {
        // Wechsler → direkt in den Umzugs-Assistenten; sonst ersten Kunden anlegen
        toast.success("Setup abgeschlossen! Lass uns deine Daten umziehen.");
        navigate("/settings?tab=migration");
      } else {
        toast.success("Setup abgeschlossen! Lege jetzt deinen ersten Kunden an.");
        navigate("/customers?create=1");
      }
    } catch (err) {
      console.error("Onboarding save error:", err);
      toast.error("Fehler beim Speichern. Bitte versuche es erneut.");
    } finally {
      setSaving(false);
    }
  };

  // Sep 2026 — Soft-Onboarding: nur das Land ist Pflicht. „Später vervollständigen"
  // speichert das Land, schließt den Wizard dauerhaft und aktiviert den Profil-Banner.
  const handleSkip = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("auth_token");
      await axios.post(
        `${API}/country/confirm-tax-residency`,
        null,
        { params: { country_code: selectedCountry }, headers: { Authorization: `Bearer ${token}` } }
      );
      await api.put("/settings", { country_code: selectedCountry });
      await api.put("/auth/me", null, { params: { onboarding_completed: true } });
      trackEvent('onboarding_skipped', { step });
      await refreshUser();
      onComplete();
      toast.success("Land gespeichert — vervollständige dein Profil jederzeit über den Hinweis unten links.");
    } catch (err) {
      console.error("Onboarding skip error:", err);
      toast.error("Fehler beim Speichern. Bitte versuche es erneut.");
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => {
    if (step < maxStep && isCurrentStepValid()) {
      trackEvent('onboarding_step_completed', { step });
      setStep(step + 1);
      return;
    }
    if (step === maxStep) handleFinish();
  };

  const prevStep = () => {
    if (step > 0) setStep(step - 1);
  };

  const isLastStep = step === maxStep;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
      data-testid="onboarding-wizard"
    >
      <div className="w-full max-w-4xl bg-white rounded-2xl shadow-[0_8px_32px_rgba(15,23,42,0.12)] border border-slate-200 overflow-hidden flex flex-col md:flex-row max-h-[92vh]">
        {/* Left Pane */}
        <div className="hidden md:flex md:w-[280px] bg-slate-900 p-8 flex-col justify-between text-white relative overflow-hidden flex-shrink-0">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.1),transparent_60%)]" />
          <div className="relative z-10">
            <h2
              className="text-xl font-bold tracking-tight mb-1"
              style={{ fontFamily: "Manrope, sans-serif" }}
            >
              ivoliq
            </h2>
            <p className="text-slate-400 text-xs leading-relaxed">
              Richte dein Finanz-Cockpit ein &mdash; in unter 2 Minuten.
            </p>
          </div>

          <div className="relative z-10 space-y-2 mt-8 flex-1">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const isActive = i === step;
              const isDone = i < step;
              return (
                <div
                  key={s.key}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? "bg-white/10"
                      : isDone
                      ? "opacity-70"
                      : "opacity-30"
                  }`}
                  data-testid={`step-indicator-${s.key}`}
                >
                  <div
                    className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-semibold transition-all ${
                      isDone
                        ? "bg-emerald-500 text-white"
                        : isActive
                        ? "bg-white text-slate-900"
                        : "bg-white/15 text-white/60"
                    }`}
                  >
                    {isDone ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <Icon className="h-3.5 w-3.5" />
                    )}
                  </div>
                  <div>
                    <p className={`text-xs font-medium ${isActive ? "text-white" : "text-slate-400"}`}>
                      {s.label}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="relative z-10 mt-auto pt-6">
            <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1.5">
              <span>Schritt {step + 1} von {steps.length}</span>
              <span>{Math.round(((step + 1) / steps.length) * 100)}%</span>
            </div>
            <div className="h-1 w-full bg-white/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-500 ease-out"
                style={{ width: `${((step + 1) / steps.length) * 100}%` }}
              />
            </div>
          </div>
        </div>

        {/* Right Pane */}
        <div className="w-full md:flex-1 p-6 sm:p-10 overflow-y-auto flex flex-col min-h-0 relative">
          {/* Sep 2026 — Schließen-Knopf, wenn aus dem Profil-Banner geöffnet */}
          {completeProfileMode && (
            <button
              type="button"
              onClick={onClose}
              className="absolute top-4 right-4 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
              data-testid="onboarding-close-btn"
              title="Schließen"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          {/* Mobile progress */}
          <div className="md:hidden flex items-center gap-3 mb-6">
            {steps.map((s, i) => (
              <div
                key={s.key}
                className={`h-1 flex-1 rounded-full transition-all ${
                  i <= step ? "bg-slate-900" : "bg-slate-200"
                }`}
              />
            ))}
          </div>

          <div className="flex-1">
            {step === 0 && (
              <StepCountry
                selectedCountry={selectedCountry}
                onSelect={setSelectedCountry}
              />
            )}
            {step === 1 && (
              <StepBusiness form={form} set={set} country={selectedCountry} />
            )}
            {step === 2 && (
              <StepBilling
                form={form}
                set={set}
                country={selectedCountry}
                logoPreview={logoPreview}
                onLogoChange={handleLogoChange}
                onLogoRemove={() => { setLogoFile(null); setLogoPreview(null); }}
              />
            )}
            {step === 3 && (
              <StepMigration wantsMigration={wantsMigration} setWantsMigration={setWantsMigration}
                lastInvoiceNumber={lastInvoiceNumber} setLastInvoiceNumber={setLastInvoiceNumber} />
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-6 mt-6 border-t border-slate-100">
            {step > 0 ? (
              <Button
                variant="outline"
                onClick={prevStep}
                className="gap-1.5 rounded-xl text-sm"
                data-testid="onboarding-back-btn"
              >
                <ChevronLeft className="h-4 w-4" />
                Zurück
              </Button>
            ) : (
              <div />
            )}
            <div className="flex items-center gap-4">
              {/* Sep 2026 — Soft-Onboarding: ab Schritt 2 überspringbar */}
              {step >= 1 && !completeProfileMode && (
                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={saving}
                  className="text-sm text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors disabled:opacity-50"
                  data-testid="onboarding-skip-btn"
                >
                  Später vervollständigen
                </button>
              )}
              <Button
                onClick={nextStep}
                disabled={saving || !isCurrentStepValid()}
                className="gap-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 px-6 text-sm"
                data-testid={isLastStep ? "onboarding-finish-btn" : "onboarding-next-btn"}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Speichert...
                  </>
                ) : isLastStep ? (
                  <>
                    {completeProfileMode ? "Profil speichern" : wantsMigration ? "Setup abschließen & umziehen" : "Setup abschließen"}
                    {wantsMigration && !completeProfileMode ? <ArrowRight className="h-4 w-4" /> : <Check className="h-4 w-4" />}
                  </>
                ) : (
                  <>
                    Weiter
                    <ChevronRight className="h-4 w-4" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Step 3: Umzug (optional) ─── */
function StepMigration({ wantsMigration, setWantsMigration, lastInvoiceNumber, setLastInvoiceNumber }) {
  const TOOLS = ["sevDesk", "Lexware Office", "DATEV", "FastBill", "Papierkram", "Excel / CSV"];
  // Live-Vorschau der nächsten Nummer (clientseitig)
  const continuationPreview = (() => {
    const v = (lastInvoiceNumber || "").trim();
    const m = v.match(/^(.*?)(\d+)(\D*)$/);
    if (!m) return null;
    const next = String(parseInt(m[2], 10) + 1).padStart(m[2].length, "0");
    return `${m[1]}${next}${m[3]}`;
  })();
  return (
    <div className="space-y-6" data-testid="onboarding-step-migration">
      <div>
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 mb-3">
          <Truck className="h-3.5 w-3.5" /> Umzugs-Assistent · kostenlos
        </div>
        <h2 className="text-2xl font-bold tracking-tight text-slate-900">Kommst du von einem anderen Tool?</h2>
        <p className="mt-2 text-sm text-slate-500">
          Bring deine Kunden, Rechnungen und Belege automatisch zu ivoliq — KI-gestützt, geführt und GoBD-konform.
          Du kannst diesen Schritt jederzeit unter <strong>Einstellungen → Umzug</strong> nachholen.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TOOLS.map((t) => (
          <span key={t} className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-600">{t}</span>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => setWantsMigration(true)}
          className={`rounded-xl border-2 p-5 text-left transition-all ${
            wantsMigration === true ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300"}`}
          data-testid="onboarding-migration-yes"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <Upload className="h-4.5 w-4.5" />
            </div>
            {wantsMigration === true && <Check className="ml-auto h-5 w-5 text-emerald-600" />}
          </div>
          <h3 className="mt-3 font-bold text-slate-900">Ja, Daten importieren</h3>
          <p className="mt-1 text-xs text-slate-500">Direkt nach dem Setup geht's in den Umzugs-Assistenten.</p>
        </button>

        <button
          type="button"
          onClick={() => setWantsMigration(false)}
          className={`rounded-xl border-2 p-5 text-left transition-all ${
            wantsMigration === false ? "border-slate-400 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
          data-testid="onboarding-migration-no"
        >
          <div className="flex items-center gap-2">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-200 text-slate-600">
              <UserPlus className="h-4.5 w-4.5" />
            </div>
            {wantsMigration === false && <Check className="ml-auto h-5 w-5 text-slate-500" />}
          </div>
          <h3 className="mt-3 font-bold text-slate-900">Nein, ich starte frisch</h3>
          <p className="mt-1 text-xs text-slate-500">Leg direkt los und erstelle deinen ersten Kunden.</p>
        </button>
      </div>

      {/* Optionale Rechnungsnummern-Fortführung für Umsteiger */}
      {wantsMigration === true && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4" data-testid="onboarding-number-continuation">
          <div className="flex items-center gap-2">
            <Hash className="h-4 w-4 text-emerald-600" />
            <h3 className="text-sm font-bold text-slate-900">Rechnungsnummern lückenlos fortsetzen?</h3>
            <span className="text-xs text-slate-400">optional</span>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Gib deine <strong>letzte Rechnungsnummer</strong> aus dem alten Tool ein — ivoliq nummeriert
            GoBD-konform ohne Lücke weiter. (Später jederzeit unter Einstellungen änderbar.)
          </p>
          <input
            type="text"
            value={lastInvoiceNumber}
            onChange={(e) => setLastInvoiceNumber(e.target.value)}
            placeholder="z.B. RE-2025-0041"
            className="mt-3 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
            data-testid="onboarding-last-invoice-number"
          />
          {continuationPreview && (
            <p className="mt-2 text-xs text-slate-600" data-testid="onboarding-continuation-preview">
              Deine nächste Rechnung: <span className="font-mono font-semibold text-emerald-700">{continuationPreview}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Step 0: Country ─── */
function StepCountry({ selectedCountry, onSelect }) {
  return (
    <div className="space-y-6" data-testid="onboarding-step-country">
      <div>
        <h2
          className="text-2xl font-bold tracking-tight text-slate-900 mb-1"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Wo bist du steuerpflichtig?
        </h2>
        <p className="text-sm text-slate-500">
          Diese Auswahl bestimmt Steuerberechnung, Währung und Rechnungsvorlagen.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3" data-testid="country-options">
        {COUNTRIES.map((c) => {
          const isSelected = selectedCountry === c.code;
          return (
            <button
              key={c.code}
              type="button"
              onClick={() => onSelect(c.code)}
              className={`flex items-center gap-4 p-5 rounded-xl border-2 transition-all text-left ${
                isSelected
                  ? "border-slate-900 bg-slate-50 shadow-sm"
                  : "border-slate-200 hover:border-slate-300 hover:bg-slate-50/50"
              }`}
              data-testid={`country-option-${c.code}`}
            >
              <CountryFlag code={c.code} isSelected={isSelected} />
              <div>
                <p className={`text-base font-semibold ${isSelected ? "text-slate-900" : "text-slate-700"}`}>
                  {c.name}
                </p>
                <p className="text-xs text-slate-500 mt-0.5">
                  {c.code === "DE" && "EUR · Einkommensteuer, Gewerbesteuer, Solidaritätszuschlag"}
                  {c.code === "AT" && "EUR · Einkommensteuer, SVS, Investitionsfreibetrag"}
                  {c.code === "CH" && "CHF · Bundessteuer, Kantonssteuer, MWST"}
                </p>
              </div>
              {isSelected && (
                <div className="ml-auto flex-shrink-0 w-6 h-6 bg-slate-900 rounded-full flex items-center justify-center">
                  <Check className="h-3.5 w-3.5 text-white" />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
        <p className="text-xs text-slate-500">
          Du kannst die steuerliche Ansässigkeit später in den Einstellungen ändern.
        </p>
      </div>
    </div>
  );
}

function CountryFlag({ code, isSelected }) {
  const flags = {
    DE: (
      <div className="w-10 h-10 rounded-lg overflow-hidden flex flex-col flex-shrink-0">
        <div className="flex-1 bg-black" />
        <div className="flex-1 bg-red-600" />
        <div className="flex-1 bg-yellow-400" />
      </div>
    ),
    AT: (
      <div className="w-10 h-10 rounded-lg overflow-hidden flex flex-col flex-shrink-0">
        <div className="flex-1 bg-red-600" />
        <div className="flex-1 bg-white" />
        <div className="flex-1 bg-red-600" />
      </div>
    ),
    CH: (
      <div className="w-10 h-10 rounded-lg overflow-hidden bg-red-600 flex items-center justify-center flex-shrink-0">
        <div className="text-white font-bold text-lg">+</div>
      </div>
    ),
  };
  return flags[code] || null;
}

/* ─── Step 1: Business ─── */
function StepBusiness({ form, set, country }) {
  return (
    <div className="space-y-6" data-testid="onboarding-step-business">
      <div>
        <h2
          className="text-2xl font-bold tracking-tight text-slate-900 mb-1"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Über dein Business
        </h2>
        <p className="text-sm text-slate-500">
          Diese Angaben werden für Rechnungen und Steuerberechnungen benötigt.
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-sm font-semibold text-slate-700">Firmenname / Name *</Label>
          <Input
            value={form.company_name}
            onChange={(e) => set("company_name", e.target.value)}
            placeholder={country === "CH" ? "z.B. Max Muster GmbH" : "z.B. Max Mustermann Consulting"}
            className="rounded-xl h-11"
            data-testid="input-company-name"
          />
        </div>

        <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50">
          <div>
            <p className="text-sm font-medium text-slate-900">
              {country === "CH" ? "MWST-befreit" : "Kleinunternehmerregelung"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              {country === "DE" ? "Umsatz unter 22.000 €/Jahr" : country === "AT" ? "Umsatz unter 35.000 €/Jahr" : "Umsatz unter 100.000 CHF/Jahr"}
            </p>
          </div>
          <Switch
            checked={form.is_kleinunternehmer}
            onCheckedChange={(v) => set("is_kleinunternehmer", v)}
            data-testid="kleinunternehmer-switch"
          />
        </div>

        {country === "AT" && !form.is_kleinunternehmer && (
          <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50" data-testid="onboarding-vstp">
            <div className="pr-3">
              <p className="text-sm font-medium text-slate-900">Vorsteuerpauschalierung (§ 14 UStG)</p>
              <p className="text-xs text-slate-500 mt-0.5">
                Optional — pauschaler Vorsteuerabzug (1,8 %). Branchensätze später in den Stammdaten wählbar.
              </p>
            </div>
            <Switch
              checked={!!form.vstp_enabled}
              onCheckedChange={(v) => set("vstp_enabled", v)}
              data-testid="onboarding-vstp-switch"
            />
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-sm font-semibold text-slate-700">
            {country === "DE" ? "USt-IdNr. / Steuernummer" : country === "AT" ? "UID-Nummer" : "MWST-Nr."}
            <span className="text-slate-400 font-normal ml-1">(optional)</span>
          </Label>
          <VATInput
            country={country}
            value={form.tax_id}
            onChange={(v) => set("tax_id", v)}
            label={null}
            className="rounded-xl h-11"
            data-testid="input-tax-id"
          />
        </div>

        {country === "DE" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-slate-700">Bundesland *</Label>
              <Select value={form.bundesland} onValueChange={(v) => set("bundesland", v)}>
                <SelectTrigger className="rounded-xl h-11" data-testid="select-bundesland">
                  <SelectValue placeholder="Bundesland wählen" />
                </SelectTrigger>
                <SelectContent>
                  {BUNDESLAENDER.map((bl) => (
                    <SelectItem key={bl.code} value={bl.code}>{bl.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50">
              <div>
                <p className="text-sm font-medium text-slate-900">Gewerbesteuerpflichtig</p>
                <p className="text-xs text-slate-500 mt-0.5">Nur für Gewerbetreibende</p>
              </div>
              <Switch checked={form.trade_tax_liable} onCheckedChange={(v) => set("trade_tax_liable", v)} data-testid="trade-tax-switch" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50">
              <div>
                <p className="text-sm font-medium text-slate-900">Kirchensteuerpflicht</p>
                <p className="text-xs text-slate-500 mt-0.5">Mitglied einer kirchensteuerpflichtigen Gemeinde</p>
              </div>
              <Switch checked={form.church_tax} onCheckedChange={(v) => set("church_tax", v)} data-testid="church-tax-switch" />
            </div>
          </>
        )}
        {country === "AT" && (
          <div className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50/50">
            <div>
              <p className="text-sm font-medium text-slate-900">SVS Opt-out</p>
              <p className="text-xs text-slate-500 mt-0.5">Befreiung von der Pflichtversicherung</p>
            </div>
            <Switch checked={form.svs_opt_out} onCheckedChange={(v) => set("svs_opt_out", v)} data-testid="svs-opt-out-switch" />
          </div>
        )}
        {country === "CH" && (
          <div className="space-y-1.5">
            <Label className="text-sm font-semibold text-slate-700">Kanton *</Label>
            <Select value={form.canton} onValueChange={(v) => set("canton", v)}>
              <SelectTrigger className="rounded-xl h-11" data-testid="select-kanton">
                <SelectValue placeholder="Kanton wählen" />
              </SelectTrigger>
              <SelectContent>
                {KANTONE.map((k) => (<SelectItem key={k} value={k}>{k}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Step 2: Billing ─── */
function StepBilling({ form, set, country, logoPreview, onLogoChange, onLogoRemove }) {
  return (
    <div className="space-y-6" data-testid="onboarding-step-billing">
      <div>
        <h2
          className="text-2xl font-bold tracking-tight text-slate-900 mb-1"
          style={{ fontFamily: "Manrope, sans-serif" }}
        >
          Rechnungsdaten
        </h2>
        <p className="text-sm text-slate-500">
          Erscheinen auf deinen Rechnungen. Später in den Einstellungen änderbar.
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">Adresse *</p>
          <div className="grid grid-cols-3 gap-2.5">
            <div className="col-span-2">
              <Input value={form.street} onChange={(e) => set("street", e.target.value)} placeholder="Straße" className="rounded-xl h-11" data-testid="input-street" />
            </div>
            <Input value={form.street_number} onChange={(e) => set("street_number", e.target.value)} placeholder="Nr." className="rounded-xl h-11" data-testid="input-street-number" />
          </div>
          <div className="grid grid-cols-3 gap-2.5 mt-2.5">
            <PostcodeInput
              country={country}
              value={form.postal_code}
              onChange={(v) => set("postal_code", v)}
              label={null}
              hint={null}
              className="rounded-xl h-11"
              data-testid="input-postal-code"
            />
            <div className="col-span-2">
              <Input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Ort" className="rounded-xl h-11" data-testid="input-city" />
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
            Bankverbindung <span className="text-slate-400 font-normal normal-case">(optional)</span>
          </p>
          <div className="space-y-2.5">
            <IBANInput
              country={country}
              value={form.iban}
              onChange={(v) => set("iban", v)}
              label={null}
              hint={null}
              className="rounded-xl h-11"
              data-testid="input-iban"
            />
            <div className="grid grid-cols-2 gap-2.5">
              <Input value={form.bic} onChange={(e) => set("bic", e.target.value.toUpperCase())} placeholder="BIC / SWIFT" className="rounded-xl h-11 font-mono text-sm tracking-wide" data-testid="input-bic" />
              <Input value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} placeholder="Bankname" className="rounded-xl h-11" data-testid="input-bank-name" />
            </div>
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2.5">
            Logo <span className="text-slate-400 font-normal normal-case">(optional)</span>
          </p>
          {logoPreview ? (
            <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-200">
              <img src={logoPreview} alt="Logo" className="w-14 h-14 object-contain rounded-lg bg-slate-50" />
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-900">Logo ausgewählt</p>
                <p className="text-xs text-slate-500">Wird auf Rechnungen angezeigt</p>
              </div>
              <Button variant="outline" size="sm" onClick={onLogoRemove} className="text-xs rounded-lg" data-testid="logo-remove-btn">Entfernen</Button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center p-5 rounded-xl border-2 border-dashed border-slate-200 hover:border-slate-300 cursor-pointer transition-colors bg-slate-50/50" data-testid="logo-upload-area">
              <Upload className="h-5 w-5 text-slate-400 mb-1.5" />
              <p className="text-sm text-slate-600">Logo hochladen</p>
              <p className="text-xs text-slate-400 mt-0.5">PNG, JPG bis 2 MB</p>
              <input type="file" accept="image/png,image/jpeg,image/jpg" className="hidden" onChange={onLogoChange} data-testid="logo-file-input" />
            </label>
          )}
        </div>
      </div>
    </div>
  );
}
